import React from 'react'
import { MICRO_TEMPLATE_STYLES, type MicroTemplate } from './microTemplates'

type Props = {
  value: string
  onChange: (id: string) => void
  templates: MicroTemplate[]
  disabled?: boolean
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={className}>
      <path
        d="M12.5 4.5L7.5 10l5 5.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={className}>
      <path
        d="M7.5 4.5L12.5 10l-5 5.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function MicroTemplatePicker({ value, onChange, templates, disabled }: Props) {
  const [style, setStyle] = React.useState<'all' | MicroTemplate['style']>('editorial')
  const railRef = React.useRef<HTMLDivElement | null>(null)
  const [canLeft, setCanLeft] = React.useState(false)
  const [canRight, setCanRight] = React.useState(false)

  const selected = templates.find((t) => t.id === value) || null
  const filtered = style === 'all' ? templates : templates.filter((t) => t.style === style)

  // If the currently-selected template is filtered out, keep it visible at the front.
  const visible = React.useMemo(() => {
    if (!selected) return filtered
    if (filtered.some((t) => t.id === selected.id)) return filtered
    return [selected, ...filtered]
  }, [filtered, selected])

  const refreshEdges = React.useCallback(() => {
    const el = railRef.current
    if (!el) return
    const max = Math.max(0, el.scrollWidth - el.clientWidth)
    const left = el.scrollLeft
    setCanLeft(left > 4)
    setCanRight(left < max - 4)
  }, [])

  React.useEffect(() => {
    refreshEdges()
  }, [refreshEdges, style, visible.length])

  React.useEffect(() => {
    window.addEventListener('resize', refreshEdges)
    return () => window.removeEventListener('resize', refreshEdges)
  }, [refreshEdges])

  function scrollByItems(dir: -1 | 1) {
    const el = railRef.current
    if (!el) return
    const delta = 140 * 4 * dir // ~3-4 thumbnails depending on spacing
    el.scrollBy({ left: delta, behavior: 'smooth' })
  }

  return (
    <div className="group">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-white/60">Template</div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-white/60">Style</div>
          <select
            value={style}
            disabled={disabled}
            onChange={(e) => setStyle((e.target.value as any) || 'all')}
            className="rounded-lg bg-black border border-white/15 px-2 py-1 text-xs text-white/90 outline-none focus:ring-2 focus:ring-brand disabled:opacity-60"
            style={{ colorScheme: 'dark' } as any}
          >
            <option value="all">All</option>
            {MICRO_TEMPLATE_STYLES.map((k) => (
              <option key={k} value={k}>
                {k[0].toUpperCase() + k.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-2 relative overflow-visible">
        {/* Desktop affordances: fades + arrows (do not block touch scrolling). */}
        <div
          className={[
            'pointer-events-none absolute inset-y-0 left-0 w-10',
            'bg-gradient-to-r from-black/40 to-transparent',
            'hidden md:block transition-opacity',
            canLeft && !disabled ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
        />
        <div
          className={[
            'pointer-events-none absolute inset-y-0 right-0 w-12',
            'bg-gradient-to-l from-black/45 to-transparent',
            'hidden md:block transition-opacity',
            canRight && !disabled ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
        />

        <button
          type="button"
          onClick={() => scrollByItems(-1)}
          disabled={!canLeft || disabled}
          className={[
            'hidden md:flex items-center justify-center',
            'absolute left-0 top-1/2 -translate-y-1/2 z-10',
            'h-9 w-9 rounded-full border border-white/15 bg-neutral-950/70 backdrop-blur',
            'text-white/90 hover:bg-neutral-950/90',
            'opacity-0 group-hover:opacity-100 transition-opacity',
            (!canLeft || disabled) ? 'pointer-events-none opacity-0' : '',
          ].join(' ')}
          aria-label="Scroll templates left"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => scrollByItems(1)}
          disabled={!canRight || disabled}
          className={[
            'hidden md:flex items-center justify-center',
            'absolute right-0 top-1/2 -translate-y-1/2 z-10',
            'h-9 w-9 rounded-full border border-white/15 bg-neutral-950/70 backdrop-blur',
            'text-white/90 hover:bg-neutral-950/90',
            'opacity-0 group-hover:opacity-100 transition-opacity',
            (!canRight || disabled) ? 'pointer-events-none opacity-0' : '',
          ].join(' ')}
          aria-label="Scroll templates right"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>

        <div
          ref={railRef}
          onScroll={refreshEdges}
          className={[
            'micro-rail',
            'px-1',
            'overflow-x-auto overflow-y-hidden no-scrollbar',
            'flex gap-3 pb-2',
            'snap-x snap-mandatory scroll-px-1',
            // End padding so the last item can scroll fully into view on desktop.
            'pr-24',
          ].join(' ')}
        >
          {visible.map((t) => {
            const active = t.id === value
            return (
              <button
                key={t.id}
                type="button"
                disabled={disabled}
                onClick={() => onChange(t.id)}
                className={[
                  'shrink-0 w-[120px] rounded-xl border overflow-hidden text-left transition snap-start',
                  active ? 'border-brand/60 ring-2 ring-brand/40' : 'border-white/10 hover:border-white/20',
                  disabled ? 'opacity-60 cursor-not-allowed' : '',
                ].join(' ')}
                aria-pressed={active}
                title={t.name}
              >
                <div className="h-[70px]" style={{ background: t.background }} />
                <div className="px-2 py-1 text-xs text-white/80 truncate">{t.name}</div>
              </button>
            )
          })}
          {/* Explicit spacer guarantees scrollable room at the end (snap-safe). */}
          <div className="shrink-0 w-24" aria-hidden="true" />
        </div>
      </div>
    </div>
  )
}
